/**
 * Module dependencies.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Button } from 'src/components/core/buttons/button';
import { ButtonWrapper, Wrapper as StepsWrapper } from 'src/components/schedule/steps';
import { ErrorMessage } from 'src/components/core/forms/styles';
import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import { axiosInstance } from 'src/core/utils/requests';
import { breakpoints } from '@untile/react-core/styles/breakpoints';
import { media } from '@untile/react-core/styles/media';
import { transparentize } from 'src/styles/utils/colors';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useEffect, useState } from 'react';
import { useSchedule } from 'src/providers/schedule';
import { useTranslation } from 'next-i18next';
import arrow from 'src/assets/svgs/arrow-bottom.svg';
import arrowRightSvg from 'src/assets/svgs/arrow-right.svg';
import styled from 'styled-components';

/**
 * `Card` styled component.
 */

const Card = styled(motion.div)`
  align-items: start;
  background-color: ${transparentize('cyan800', 0.16)};
  border: 2px solid transparent;
  border-radius: var(--border-radius-xs);
  display: grid;
  grid-gap: var(--space-sm);
  padding: var(--space-xs);

  ${media.min.md`
    grid-template-columns: repeat(2, calc(50% - var(--space-xxs)));
    grid-gap: var(--space-xs);
  `}
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: grid;
  grid-gap: var(--space-xxs);
  grid-template-columns: 100%;
  width: 100%;
`;

/**
 * `Label` styled component.
 */

const Label = styled(Text).attrs({ variant: 'labelS' })`
  color: var(--color-slate300);
`;

/**
 * `SelectWrapper` styled component.
 */

const SelectWrapper = styled.div`
  position: relative;
`;

/**
 * `SelectInput` styled component.
 */

const SelectInput = styled.select`
  appearance: none;
  background-color: transparent;
  border: 1px solid var(--color-slate100);
  border-radius: var(--border-radius-xxs);
  color: var(--color-slate300);
  cursor: pointer;
  font-family: var(--font-family-open-sans);
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.032px;
  line-height: 24px;
  padding: var(--space-xxs) 38px var(--space-xxs) var(--space-xs);
  width: 100%;

  ${({ disabled }) =>
    disabled &&
    `
      border: 1px solid rgba(21, 46, 74, 0.20);
      color: var(--color-slate500);
      cursor: not-allowed;
    `}
`;

/**
 * `IconWrapper` styled component.
 */

const IconWrapper = styled.div<{ disabled?: boolean }>`
  align-items: center;
  color: var(--color-slate300);
  display: flex;
  height: 100%;
  pointer-events: none;
  position: absolute;
  right: var(--space-xs);
  top: 0%;

  ${({ disabled }) =>
    disabled &&
    `
      color: var(--color-slate500);
    `}
`;

/**
 * `DistrictInput` component.
 */

const DistrictInput = ({ hasError }: { hasError: boolean }) => {
  const { t } = useTranslation('schedule');
  const { districts, formData, setCounties, setStep, updateFormData } = useSchedule();

  useEffect(() => {
    if (districts?.length === 0) {
      setStep(4);
    }
  }, [districts, setStep]);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedDistrict = districts?.find(district => district.districtName === event.target.value);

    if (selectedDistrict) {
      updateFormData('district', selectedDistrict);

      updateFormData('county', {
        countyCode: '',
        countyName: '',
        districtCode: '',
        districtName: ''
      });

      setCounties([]);

      axiosInstance
        .post('/counties', {
          districtCode: selectedDistrict.districtCode
        })
        .then(({ data }) => {
          setCounties(data.data);
        })
        .catch(() => {
          setStep(4);
        });
    }
  };

  return (
    <Wrapper>
      <Label>{t('steps.1.district.label')}</Label>
      <SelectWrapper>
        <SelectInput
          defaultValue={formData.district.districtName || t('steps.1.district.placeholder')}
          onChange={event => handleChange(event)}
        >
          <option disabled hidden value={t('steps.1.district.placeholder')}>
            {t('steps.1.district.placeholder')}
          </option>
          {districts?.map(district => (
            <option key={district.districtCode} value={district.districtName}>
              {district.districtName}
            </option>
          ))}
        </SelectInput>
        <IconWrapper>
          <Svg icon={arrow} size={'20px'} />
        </IconWrapper>
      </SelectWrapper>
      {hasError && <ErrorMessage>{t('form.rules.required')}</ErrorMessage>}
    </Wrapper>
  );
};

const CountyInput = ({ hasError }: { hasError: boolean }) => {
  const { t } = useTranslation('schedule');
  const { counties, formData, updateFormData } = useSchedule();
  const [selectedCounty, setSelectedCounty] = useState<string>('dafaultValue');

  useEffect(() => {
    setSelectedCounty('dafaultValue');
  }, [formData.district, updateFormData]);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const countyName = event.target.value;
    setSelectedCounty(countyName);

    const selectedCounty = counties.find(county => county.countyName === countyName);

    if (selectedCounty) {
      updateFormData('county', selectedCounty);
    }
  };

  return (
    <Wrapper>
      <Label>{t('steps.1.county.label')}</Label>
      <SelectWrapper>
        <SelectInput
          disabled={counties.length === 0}
          onChange={event => handleChange(event)}
          value={formData.county.countyName || selectedCounty}
        >
          <option disabled hidden value={'dafaultValue'}>
            {t('steps.1.county.placeholder')}
          </option>
          {counties &&
            counties.map(county => (
              <option key={county.countyCode} value={county.countyName}>
                {county.countyName}
              </option>
            ))}
        </SelectInput>
        <IconWrapper disabled={!counties || counties.length === 0}>
          <Svg icon={arrow} size={'20px'} />
        </IconWrapper>
      </SelectWrapper>
      {hasError && <ErrorMessage>{t('form.rules.required')}</ErrorMessage>}
    </Wrapper>
  );
};

/**
 * Export `UserLocation` component.
 */

export const UserLocation = () => {
  const isMobile = useBreakpoint(0, breakpoints.md - 1);
  const { t } = useTranslation();
  const { formData, setStep } = useSchedule();
  const [errors, setErrors] = useState({ county: false, district: false });

  useEffect(() => {
    if (formData.district.districtName !== '') {
      setErrors(prevErrors => ({
        ...prevErrors,
        district: !formData.district.districtName
      }));
    }
  }, [formData.district.districtName]);

  useEffect(() => {
    if (formData.county.countyName !== '') {
      setErrors(prevErrors => ({
        ...prevErrors,
        county: !formData.county.countyName
      }));
    }
  }, [formData.county.countyName]);

  const handleClick = () => {
    const newErrors = {
      county: !formData.county.countyName,
      district: !formData.district.districtName
    };

    setErrors(newErrors);

    if (!newErrors.county && !newErrors.district) {
      setStep(2);
    }
  };

  const animationProps = {
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    initial: { opacity: 0 }
  };

  return (
    <div>
      <StepsWrapper style={{ height: 'fit-content' }}>
        <AnimatePresence mode={'wait'}>
          <Card {...animationProps}>
            <DistrictInput hasError={errors.district} />

            <CountyInput hasError={errors.county} />

            {isMobile && (
              <ButtonWrapper>
                <Button
                  aria-label={t('common:actions.continue')}
                  icon={arrowRightSvg}
                  onClick={() => handleClick()}
                  reverse
                  size={'medium'}
                  type={'submit'}
                >
                  {t('common:actions.continue')}
                </Button>
              </ButtonWrapper>
            )}
          </Card>
        </AnimatePresence>
      </StepsWrapper>

      {!isMobile && (
        <ButtonWrapper>
          <AnimatePresence mode={'wait'}>
            <Button
              aria-label={t('common:actions.continue')}
              icon={arrowRightSvg}
              onClick={() => handleClick()}
              reverse
              size={'medium'}
              type={'submit'}
            >
              {t('common:actions.continue')}
            </Button>
          </AnimatePresence>
        </ButtonWrapper>
      )}
    </div>
  );
};
